/**
 * PaperSessionReviewModule — Paper Session Workflow V4 (Part A).
 *
 * Standalone Owner Review section (mounted as a Strategy Lab tab). Lists paper-session
 * DRAFTS with a status filter (all / ready_for_owner_review / blocked / reviewed) and
 * per-row actions: open (in the global drawer), mark reviewed, delete, export notes.
 * Read-only review surface: NEVER renders Submit/Buy/Sell/Execute and never calls a
 * broker/order endpoint. No banners.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, Eye, CheckCircle2, Trash2, FileText, RefreshCw, Loader2 } from "lucide-react";
import {
  listDrafts, patchDraft, deleteDraft, computeRisk, type PaperDraft,
} from "../lib/paperSessionDrafts";

type Filter = "all" | "ready_for_owner_review" | "blocked" | "reviewed";
const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ready_for_owner_review", label: "Ready for review" },
  { id: "blocked", label: "Blocked" },
  { id: "reviewed", label: "Reviewed" },
];

function money(v: number | null): string { return v === null || v === undefined ? "—" : "$" + v.toLocaleString(undefined, { maximumFractionDigits: 2 }); }
function fmtTime(ts?: string | null): string {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return String(ts); }
}
function statusLabel(s?: string | null): string {
  if (s === "ready_for_owner_review") return "Ready for review";
  if (s === "blocked") return "Blocked";
  return s || "—";
}

export default function PaperSessionReviewModule() {
  const [filter, setFilter] = useState<Filter>("ready_for_owner_review");
  const [rows, setRows] = useState<PaperDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // 'reviewed' filters on review_status (client-side); the others use the backend
      // validation_status filter.
      const statusQuery = filter === "all" || filter === "reviewed" ? undefined : filter;
      let items = await listDrafts(statusQuery);
      if (filter === "reviewed") items = items.filter((d) => d.review_status === "reviewed");
      setRows(items);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { void refresh(); }, [refresh]);

  const openDraft = useCallback((d: PaperDraft) => {
    try { window.dispatchEvent(new CustomEvent("adversyn:open-paper-draft", { detail: d })); } catch { /* ignore */ }
  }, []);

  const onMarkReviewed = useCallback(async (id: string) => {
    const u = await patchDraft(id, { review_status: "reviewed" });
    if (u) { setMsg(`Marked reviewed: ${u.symbol}`); void refresh(); }
  }, [refresh]);

  const onDelete = useCallback(async (id: string) => {
    await deleteDraft(id);
    setMsg("Draft deleted.");
    void refresh();
  }, [refresh]);

  const onExport = useCallback((d: PaperDraft) => {
    const integ = (d.integrity_snapshot || {}) as Record<string, any>;
    const cat = (d.catalyst_snapshot || {}) as Record<string, any>;
    const rk = computeRisk({ side: d.side, entry: d.entry, stop: d.stop_loss, size: d.size, target: d.target });
    const lines = [
      `PAPER SESSION DRAFT — ${d.symbol}`,
      `Generated: ${new Date().toISOString()}`,
      "",
      `Account: ${d.account_label || "—"}   Side: ${d.side || "—"}`,
      `Entry: ${d.entry ?? "—"}   Stop: ${d.stop_loss ?? "—"}   Target: ${d.target ?? "—"}   Size: ${d.size ?? "—"}`,
      `Exit rule: ${d.exit_rule || "—"}`,
      `Position value: ${money(rk.positionValue)}   $ risk: ${money(rk.maxLoss)}   R:R ${rk.rewardRiskRatio !== null ? rk.rewardRiskRatio.toFixed(2) : "—"}`,
      `Integrity band: ${integ.integrity_band || "—"}   Catalyst: ${cat.catalyst_status || "—"}`,
      `Validation: ${d.validation_status || "—"}   Review: ${d.review_status || "—"}`,
      `Thesis: ${d.thesis || "—"}`,
      "",
      "NOTE: Draft only. No order placed. Owner authorization required before any future submission.",
    ];
    try {
      const blob = new Blob([lines.join("\n")], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `paper-draft-${d.symbol || "draft"}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg("Notes exported.");
    } catch { setMsg("Export failed."); }
  }, []);

  const counts = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-4" data-testid="owner-review-module">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="text-[var(--muted-text)]" />
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">Owner Review</h3>
          <span data-testid="owner-review-count" className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">({counts})</span>
        </div>
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`owner-review-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${filter === f.id ? "bg-[var(--app-text)] text-[var(--card-bg)] border-[var(--app-text)]" : "text-[var(--muted-text)] border-[var(--card-border)] hover:text-[var(--app-text)]"}`}
            >
              {f.label}
            </button>
          ))}
          <button type="button" data-testid="owner-review-refresh" onClick={() => void refresh()} disabled={loading} title="Refresh" className="ml-1 p-1.5 rounded-lg border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] disabled:opacity-50">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Source: /api/v1/paper-session/drafts · Review-only. Adversyn never places orders on your behalf.
      </p>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]"><Loader2 size={14} className="inline animate-spin mr-2" />Loading drafts…</div>
        ) : rows.length === 0 ? (
          <div data-testid="owner-review-empty" className="p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">No drafts for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="owner-review-table">
              <thead>
                <tr className="bg-[var(--card-header-bg)] border-b border-[var(--card-header-border)] text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] text-left">
                  <th className="px-3 py-2">Symbol</th>
                  <th className="px-3 py-2">Side</th>
                  <th className="px-3 py-2 hidden md:table-cell">Account</th>
                  <th className="px-3 py-2">Validation</th>
                  <th className="px-3 py-2 hidden lg:table-cell">$ Risk</th>
                  <th className="px-3 py-2 hidden lg:table-cell">Pos. value</th>
                  <th className="px-3 py-2 hidden xl:table-cell">Integrity</th>
                  <th className="px-3 py-2 hidden xl:table-cell">Catalyst</th>
                  <th className="px-3 py-2 hidden md:table-cell">Updated</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const integ = (d.integrity_snapshot || {}) as Record<string, any>;
                  const cat = (d.catalyst_snapshot || {}) as Record<string, any>;
                  const rk = computeRisk({ side: d.side, entry: d.entry, stop: d.stop_loss, size: d.size, target: d.target });
                  return (
                    <tr key={d.draft_id} data-testid="owner-review-row" data-symbol={d.symbol} data-validation={d.validation_status || ""} data-review={d.review_status || ""} className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)] transition-colors">
                      <td className="px-3 py-2 text-sm font-black text-[var(--app-text)] italic">{d.symbol}</td>
                      <td className="px-3 py-2 text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">{d.side || "—"}</td>
                      <td className="px-3 py-2 text-xs font-bold text-[var(--muted-text)] hidden md:table-cell">{d.account_label || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${d.validation_status === "ready_for_owner_review" ? "text-emerald-600" : d.validation_status === "blocked" ? "text-rose-600" : "text-[var(--muted-text)]"}`}>{statusLabel(d.validation_status)}</span>
                        {d.review_status === "reviewed" && <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-[var(--app-text)]">· Reviewed</span>}
                      </td>
                      <td className="px-3 py-2 text-xs font-bold text-[var(--app-text)] tabular-nums hidden lg:table-cell">{money(rk.maxLoss)}</td>
                      <td className="px-3 py-2 text-xs font-bold text-[var(--app-text)] tabular-nums hidden lg:table-cell">{money(rk.positionValue)}</td>
                      <td className="px-3 py-2 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest hidden xl:table-cell">{integ.integrity_band || "—"}</td>
                      <td className="px-3 py-2 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest hidden xl:table-cell">{cat.catalyst_status || "—"}</td>
                      <td className="px-3 py-2 text-[10px] font-bold text-[var(--muted-text)] hidden md:table-cell">{fmtTime(d.updated_ts)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid="owner-review-open" onClick={() => openDraft(d)} title="Open in planner" className="p-1 rounded hover:bg-[var(--card-border)]/40 text-[var(--muted-text)]"><Eye size={14} /></button>
                          <button data-testid="owner-review-mark" onClick={() => void onMarkReviewed(d.draft_id)} title="Mark reviewed" className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600"><CheckCircle2 size={14} /></button>
                          <button data-testid="owner-review-export" onClick={() => onExport(d)} title="Export notes" className="p-1 rounded hover:bg-[var(--input-bg)] text-[var(--muted-text)]"><FileText size={14} /></button>
                          <button data-testid="owner-review-delete" onClick={() => void onDelete(d.draft_id)} title="Delete" className="p-1 rounded hover:bg-rose-500/10 text-rose-600"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {msg && <div data-testid="owner-review-msg" className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{msg}</div>}
    </div>
  );
}
